/**
 * @fileoverview A small in-memory buffer of recently shown toast messages.
 *
 * Toast notifications are rendered in a top-level container outside the page
 * regions that the Operator page scraper reads, and auto-closing toasts
 * (info/success/warning) disappear after ~10s. To let the BrazeAI Operator
 * understand what just happened (e.g. "the preview timed out") even after a
 * toast has auto-closed, every toast is recorded here as it is shown and the
 * page scraper folds recent entries into the page state it reports to the LLM.
 *
 * This lives behind the single `makeToast` chokepoint, so it captures every
 * toast generically with no per-feature wiring.
 */

import type { ToastIntent } from "./types";

export type RecentToast = {
  intent: ToastIntent;
  text: string;
  /** Epoch milliseconds when the toast was shown. */
  timestamp: number;
};

/** Maximum number of recent toasts retained. Oldest entries are evicted. */
const maxRecentToasts = 5;

/** Default max age (ms) for `getRecentToasts`. */
export const defaultRecentToastMaxAgeMs = 60_000;

/** Newest entries are kept at the front of the buffer. */
let recentToasts: RecentToast[] = [];

/**
 * Record a toast as it is shown. Only string content is recorded; toasts with
 * React-node content are skipped to avoid recording opaque/noisy entries.
 */
export function recordToast(intent: ToastIntent, content: unknown): void {
  if (typeof content !== "string") {
    return;
  }
  const text = content.trim();
  if (!text) {
    return;
  }
  // Date.now() is used (rather than getNowTimestamp) because this @lib module
  // must not depend on @services, and we only need a monotonic recency marker.
  recentToasts = [{ intent, text, timestamp: Date.now() }, ...recentToasts].slice(0, maxRecentToasts);
}

/**
 * Return recent toasts newer than `maxAgeMs`, most-recent-first.
 */
export function getRecentToasts(maxAgeMs: number = defaultRecentToastMaxAgeMs): RecentToast[] {
  const cutoff = Date.now() - maxAgeMs;
  return recentToasts.filter((toast) => toast.timestamp >= cutoff);
}

/** Clear the buffer. Intended for tests. */
export function clearRecentToasts(): void {
  recentToasts = [];
}
